import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase';
import Header from './Header';
import './Manager.css';

function HomeSlidesManager() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);
  const [formData, setFormData] = useState({
    icon: '',
    title: '',
    description: '',
    color: '#FF1493',
    order: 1,
  });

  useEffect(() => {
    loadSlides();
  }, []);

  const loadSlides = async () => {
    try {
      const q = query(collection(db, 'homeSlides'), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      const slidesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSlides(slidesData);
    } catch (error) {
      console.error('Error loading slides:', error);
      alert('Failed to load slides');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingSlide) {
        await updateDoc(doc(db, 'homeSlides', editingSlide.id), {
          ...formData,
          updatedAt: new Date().toISOString(),
        });
        alert('Slide updated successfully!');
      } else {
        await addDoc(collection(db, 'homeSlides'), {
          ...formData,
          createdAt: new Date().toISOString(),
        });
        alert('Slide added successfully!');
      }
      setShowModal(false);
      setEditingSlide(null);
      resetForm();
      loadSlides();
    } catch (error) {
      console.error('Error saving slide:', error);
      alert('Failed to save slide');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (slide) => {
    setEditingSlide(slide);
    setFormData({
      icon: slide.icon,
      title: slide.title,
      description: slide.description,
      color: slide.color,
      order: slide.order,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this slide?')) return;

    try {
      await deleteDoc(doc(db, 'homeSlides', id));
      alert('Slide deleted successfully!');
      loadSlides();
    } catch (error) {
      console.error('Error deleting slide:', error);
      alert('Failed to delete slide');
    }
  };

  const resetForm = () => {
    setFormData({
      icon: '',
      title: '',
      description: '',
      color: '#FF1493',
      order: 1,
    });
  };

  return (
    <div>
      <Header />

      <div className="container">
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Home Slides Management</h2>
            <button
              onClick={() => {
                setEditingSlide(null);
                resetForm();
                setShowModal(true);
              }}
              className="btn btn-primary"
            >
              + Add New Slide
            </button>
          </div>

          {loading && <p>Loading...</p>}

          {!loading && slides.length === 0 && (
            <p>No slides found. Add your first slide!</p>
          )}

          {!loading && slides.length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Icon</th>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Color</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {slides.map((slide) => (
                  <tr key={slide.id}>
                    <td>{slide.order}</td>
                    <td>{slide.icon}</td>
                    <td>{slide.title}</td>
                    <td>{slide.description}</td>
                    <td>
                      <span
                        style={{
                          display: 'inline-block',
                          width: '30px',
                          height: '30px',
                          backgroundColor: slide.color,
                          borderRadius: '4px',
                        }}
                      ></span>
                    </td>
                    <td className="actions">
                      <button
                        onClick={() => handleEdit(slide)}
                        className="btn btn-secondary"
                        style={{ marginRight: '8px' }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(slide.id)}
                        className="btn btn-danger"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="info-box">
          <h3>Icon Names</h3>
          <p>Use Ionicons names: heart, school, shield-checkmark, trophy, people, etc.</p>
          <a href="https://ionic.io/ionicons" target="_blank" rel="noopener noreferrer">
            View all icons →
          </a>
        </div>
      </div>

      {showModal && (
        <div className="modal" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingSlide ? 'Edit Slide' : 'Add New Slide'}
              </h2>
              <button
                className="close-btn"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Icon Name *</label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  placeholder="e.g., heart, school, shield-checkmark"
                  required
                />
              </div>

              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter slide title"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter slide description"
                  required
                />
              </div>

              <div className="form-group">
                <label>Color *</label>
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Order *</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  min="1"
                  required
                />
              </div>

              <div className="actions">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : editingSlide ? 'Update Slide' : 'Add Slide'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingSlide(null);
                    resetForm();
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default HomeSlidesManager;
